import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FiservCardInterface,
  FiservCardStatusEnum,
  Inscription,
  Preloader,
  Text,
  WarningCard,
  WarningIcon,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';

import { CardAccessListItem } from './CardAccessListItem';

interface Props {
  card?: FiservCardInterface;
  lockedPursesIds: string[];
  isCardLocked?: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onChangeStatusMutationHandler: (status: FiservCardStatusEnum, lockedPurseIds?: string[]) => void;
}

export const CardAccessConfirmationModal: React.FC<Props> = ({
  card,
  lockedPursesIds,
  isCardLocked,
  isLoading,
  onCancel,
  onChangeStatusMutationHandler,
}) => {
  const { t } = useTranslation();

  const activePurses = useMemo(() => {
    return card?.cardPurses?.filter((purse) => !lockedPursesIds.includes(purse.id)) || [];
  }, [card?.cardPurses, lockedPursesIds]);

  const lockedPurses = useMemo(() => {
    return card?.cardPurses?.filter((purse) => lockedPursesIds.includes(purse.id)) || [];
  }, [card?.cardPurses, lockedPursesIds]);

  return (
    <Box direction="column">
      <Box round="moduleRound" pad="l" background="module">
        <Box>
          <Text color="textBody" weight="bold" margin={{ top: 'xs', bottom: 'xs' }}>{t(isCardLocked ? 'Card to be locked:' : 'Card to be unlocked:')}</Text>
          <CardAccessListItem
            card={card}
            withoutToggle
          />
          {
            activePurses?.length ? (
              <>
                <Text color="textBody" weight="bold" margin={{ top: 'xl', bottom: 'xs' }}>{t('Accounts to be unlocked:')}</Text>
                {
                  activePurses?.map((purse) => (
                    <CardAccessListItem
                      key={purse.id}
                      purse={purse}
                      withoutToggle
                    />
                  ))
                }
              </>
            ) : null
          }
          {
            lockedPurses?.length ? (
              <>
                <Text color="textBody" weight="bold" margin={{ top: 'xl', bottom: 'xs' }}>{t('Accounts to be locked:')}</Text>
                {
                  lockedPurses?.map((purse) => (
                    <CardAccessListItem
                      key={purse.id}
                      purse={purse}
                      withoutToggle
                    />
                  ))
                }
              </>
            ) : null
          }
          <Box margin={{ top: 'xs' }}>
            <WarningCard
              icon={<WarningIcon />}
              title={<Inscription weight="bold" lineHeight="20px" color="textBody">{t('The account(s) selected will be locked for the primary card as well as any dependent cards.')}</Inscription>}
              topBorderColor="transparent"
              containerProps={{ height: '72px', pad: { horizontal: 'l', vertical: 'm' }, round: 'container2Round' }}
            />
          </Box>
        </Box>
      </Box>
      <Box
        direction="row"
        fill="horizontal"
        margin={{ top: 'l' }}
        align="end"
        justify="end"
        width="medium"
        gap="xs"
      >
        <AppButton
          buttonType="secondary"
          width="140px"
          type="button"
          onClick={onCancel}
        >
          {t('Cancel')}
        </AppButton>
        <AppButton
          disabled={isLoading}
          type="button"
          onClick={() => onChangeStatusMutationHandler(isCardLocked ? FiservCardStatusEnum.FROZEN : FiservCardStatusEnum.ACTIVE, lockedPurses.map(purse => purse.id))}
          width="140px"
          color="blue"
        >
          {isLoading ? <Preloader color="white" /> : t('Confirm')}
        </AppButton>
      </Box>
    </Box>
  );
};
